import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { 
  Zap, Check, ShieldCheck, CreditCard, Receipt, 
  Tag, X, Printer, FileText, Percent, RefreshCw, ExternalLink, 
  Calendar, Building2, Search, ChevronDown, MapPin
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SELLER_DETAILS = {
  company: "EXPOLEAD",
  address: "68-A, PUSHPRAJ COMPLEX, VATVA, AHMEDABAD, GUJARAT, INDIA-382445",
  gstin: "24ALLPD3750G1ZD",
  sac: "998313 (IT Services)"
};

const subscriptionPlans = [
  {
    id: "event_lite",
    name: "Event Lite",
    amount: 2299,
    duration: "Per Event (3 Days)",
    description: "Ideal for short 2-3 day exhibitions.",
    features: [
      "Up to 350 AI Business Card Scans",
      "350 WhatsApp Messages Included",
      "Zero Meta API Bill (Covered by ExpoLead)",
      "Valid for 3-Day Exhibition Pass",
      "Real-time Team Sharing & Excel Export"
    ],
    popular: false,
  },
  {
    id: "event_pro",
    name: "Event Pro",
    amount: 6500,
    duration: "Per Event (5 Days)",
    description: "Designed for full-week major expos.",
    features: [
      "Up to 2,000 AI Business Card Scans",
      "2,000 WhatsApp Messages Included",
      "Zero Meta API Bill (Covered by ExpoLead)",
      "Valid for 5-Day Exhibition Pass",
      "Priority OCR, Team Sync & Excel Export"
    ],
    popular: true,
  }
];

export default function Payment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useAuth();
  
  const [selectedPlanId, setSelectedPlanId] = useState("event_lite");
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const eventDropdownRef = useRef(null);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [gstNumber, setGstNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const isNativeApp = Capacitor.isNativePlatform();

  const [invoices, setInvoices] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setIsEventDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAvailableEvents = useCallback(async () => {
    try {
      const res = await api.get("/events");
      const validEvents = Array.isArray(res.data) ? res.data : [];
      setEventsList(validEvents);

      const urlEventId = searchParams.get("eventId");
      if (urlEventId && validEvents.some(e => e.id === urlEventId)) {
        setSelectedEventId(urlEventId);
      } else if (validEvents.length > 0) {
        setSelectedEventId((prev) => prev || validEvents[0].id);
      }
    } catch (err) {
      console.error("Failed to load events", err);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAvailableEvents();
  }, [fetchAvailableEvents]);

  useEffect(() => {
    const planParam = searchParams.get("plan");
    const couponParam = searchParams.get("coupon");
    const gstParam = searchParams.get("gst");

    if (planParam && subscriptionPlans.some((p) => p.id === planParam)) {
      setSelectedPlanId(planParam);
    }
    if (couponParam) setCouponCode(couponParam);
    if (gstParam) setGstNumber(gstParam);
    else if (user?.gst_number) setGstNumber(user.gst_number);
  }, [searchParams, user]);

  useEffect(() => {
    if (!isNativeApp) {
      const scriptId = "razorpay-checkout-js";
      if (!document.getElementById(scriptId)) {
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onerror = () => {
          toast.error("Failed to load payment gateway script. Please check your internet connection.");
        };
        document.body.appendChild(script);
      }
    }
  }, [isNativeApp]);

  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchBillHistory = useCallback(async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const res = await api.get("/payments/history");
      if (isMountedRef.current) setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load bill history");
    } finally {
      if (isMountedRef.current) setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBillHistory();
  }, [fetchBillHistory]);

  const currentPlan = subscriptionPlans.find((p) => p.id === selectedPlanId) || subscriptionPlans[0];
  const activeSelectedEvent = eventsList.find((e) => e.id === selectedEventId);

  const filteredEventsList = useMemo(() => {
    const q = eventSearchQuery.toLowerCase().trim();
    if (!q) return eventsList;
    return eventsList.filter((ev) => 
      (ev.title || "").toLowerCase().includes(q) ||
      (ev.location || "").toLowerCase().includes(q)
    );
  }, [eventsList, eventSearchQuery]);

  const basePrice = currentPlan.amount;
  const discountPercent = appliedCoupon ? appliedCoupon.discountPercent : 0;
  const discountAmount = Math.round(basePrice * (discountPercent / 100));
  const taxableAmount = Math.max(0, basePrice - discountAmount);
  const gstAmount = Math.round(taxableAmount * 0.18);
  const finalTotal = taxableAmount + gstAmount;

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    try {
      const res = await api.post("/payments/validate-coupon", {
        coupon_code: couponCode.trim(),
        plan_id: selectedPlanId
      });

      if (res.data.valid) {
        setAppliedCoupon({
          code: res.data.coupon_code,
          discountPercent: res.data.discount_percent
        });
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to validate coupon.");
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Coupon removed.");
  };

  const syncUpdatedUser = async () => {
    try {
      const userRes = await api.get("/auth/me");
      if (userRes?.data) {
        setUser(userRes.data);
        localStorage.setItem("ls_user", JSON.stringify(userRes.data));
      }
    } catch (err) {
      console.error("Failed to sync user after purchase", err);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate(`/auth/login?redirect=/payment?plan=${selectedPlanId}&eventId=${selectedEventId}`);
      return;
    }

    if (!selectedEventId) {
      toast.error("Please select an exhibition/event before continuing.");
      return;
    }

    setLoading(true);

    if (isNativeApp) {
      try {
        const webUrl = `https://expolead.thensgroup.in/payment?plan=${selectedPlanId}&eventId=${selectedEventId}${
          appliedCoupon ? `&coupon=${appliedCoupon.code}` : ""
        }${gstNumber ? `&gst=${encodeURIComponent(gstNumber.trim())}` : ""}`;

        toast.info("Opening secure payment gateway...");
        await Browser.open({ url: webUrl });
      } catch (err) {
        toast.error("Could not launch external browser.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Generate unique client idempotency key for this checkout attempt[cite: 11]
    const clientOrderIdempotencyKey = 
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `ord_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    try {
      const orderRes = await api.post(
        "/payments/create-order",
        {
          plan_id: selectedPlanId,
          event_id: selectedEventId,
          coupon_code: appliedCoupon ? appliedCoupon.code : null,
          gst_number: gstNumber.trim() || null
        },
        {
          headers: {
            "Idempotency-Key": clientOrderIdempotencyKey
          }
        }
      );

      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "ExpoLead (NS Engineers)",
        description: `${currentPlan.name} Subscription`,
        order_id: order_id,
        prefill: {
          name: user.name || "",
          email: user.email || "",
          contact: user.phone || ""
        },
        theme: {
          color: "#09090b"
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.info("Payment window closed.");
          }
        },
        handler: async function (response) {
          try {
            setLoading(true);
            const verifyRes = await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlanId,
              event_id: selectedEventId,
              coupon_code: appliedCoupon ? appliedCoupon.code : null,
              gst_number: gstNumber.trim() || null
            });

            toast.success("Payment successful! Plan activated.");
            await syncUpdatedUser();
            fetchBillHistory();
            setSelectedInvoice(verifyRes.data);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed.");
          } finally {
            setLoading(false);
          }
        }
      };

      if (typeof window.Razorpay !== "function") {
        toast.error("Payment SDK is not loaded. Please refresh the page and try again.");
        setLoading(false);
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setLoading(false);
        toast.error(response?.error?.description || "Payment failed at gateway.");
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to initiate transaction.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4 sm:px-6 lg:px-8 pb-32 font-sans">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* HEADER */}
        <div className="flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 text-white text-xs font-semibold tracking-wide">
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            <span>EXPOLEAD PLANS & PRICING</span>
          </div>

          <button
            onClick={() => {
              fetchBillHistory();
              setShowHistoryModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 text-zinc-800 hover:text-blue-600 font-bold text-xs rounded-2xl shadow-sm transition cursor-pointer"
          >
            <Receipt className="w-4 h-4 text-blue-600" />
            <span>Bill History ({invoices.length})</span>
          </button>
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-3xl font-extrabold text-zinc-900 tracking-tight">
            Select Your Event & Plan
          </h1>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            Choose an exhibition to attach your subscription.
          </p>
        </div>

        {/* SEARCHABLE EVENT SELECTOR[cite: 11] */}
        <div className="bg-white rounded-3xl p-5 border-2 border-blue-600 shadow-sm space-y-2 relative" ref={eventDropdownRef}>
          <label className="text-xs font-bold text-zinc-800 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span>Select Exhibition / Event (Mandatory) *</span>
            </span>
            <span className="text-[10px] text-blue-600 uppercase font-black tracking-wider bg-blue-50 px-2.5 py-0.5 rounded-full">
              Plan Links To Event
            </span>
          </label>

          <button
            type="button"
            onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
            className="w-full px-4 py-3.5 bg-zinc-50 hover:bg-zinc-100/80 border border-zinc-200 rounded-2xl text-left flex items-center justify-between transition focus:ring-2 focus:ring-blue-600 cursor-pointer"
          >
            {activeSelectedEvent ? (
              <div className="min-w-0 pr-2">
                <p className="text-xs font-bold text-zinc-900 truncate">
                  {activeSelectedEvent.title}
                </p>
                <p className="text-[11px] text-zinc-500 font-medium flex items-center gap-1.5 mt-0.5">
                  <MapPin className="w-3 h-3 text-rose-500 shrink-0" />
                  <span className="truncate">{activeSelectedEvent.location}</span>
                  <span>•</span>
                  <span>{new Date(activeSelectedEvent.start_date).toLocaleDateString()} to {new Date(activeSelectedEvent.end_date).toLocaleDateString()}</span>
                </p>
              </div>
            ) : (
              <span className="text-xs font-medium text-zinc-400">
                -- Search or choose an exhibition from directory --
              </span>
            )}
            <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${isEventDropdownOpen ? "rotate-180" : ""}`} />
          </button>

          {isEventDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200 rounded-2xl shadow-xl z-50 p-2 space-y-2 animate-in fade-in zoom-in-95 duration-100">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Type event name or city to search..."
                  value={eventSearchQuery}
                  onChange={(e) => setEventSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-medium text-zinc-900 placeholder-zinc-400 outline-none focus:border-blue-600"
                />
              </div>

              <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
                {filteredEventsList.length === 0 ? (
                  <div className="py-6 text-center text-xs text-zinc-400 font-medium">
                    No matching exhibitions found.
                  </div>
                ) : (
                  filteredEventsList.map((ev) => {
                    const isSelected = selectedEventId === ev.id;
                    return (
                      <div
                        key={ev.id}
                        onClick={() => {
                          setSelectedEventId(ev.id);
                          setIsEventDropdownOpen(false);
                          setEventSearchQuery("");
                        }}
                        className={`p-2.5 rounded-xl cursor-pointer flex items-center justify-between transition ${
                          isSelected 
                            ? "bg-blue-50 border border-blue-200 text-blue-900" 
                            : "hover:bg-zinc-100 text-zinc-800"
                        }`}
                      >
                        <div className="min-w-0 pr-2">
                          <p className="text-xs font-bold truncate">{ev.title}</p>
                          <p className="text-[10px] text-zinc-500 font-medium flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3 text-rose-500 shrink-0" />
                            <span>{ev.location}</span>
                            <span>•</span>
                            <span>{new Date(ev.start_date).toLocaleDateString()} to {new Date(ev.end_date).toLocaleDateString()}</span>
                          </p>
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* PRICING CARDS[cite: 11] */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {subscriptionPlans.map((plan) => {
            const isSelected = selectedPlanId === plan.id;
            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlanId(plan.id)}
                className={`relative bg-white rounded-3xl p-5 border-2 transition-all cursor-pointer flex flex-col justify-between shadow-sm hover:shadow-md ${
                  isSelected
                    ? "border-zinc-950 ring-2 ring-zinc-950/10"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 right-4 bg-blue-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                    Most Popular
                  </span>
                )}

                <div>
                  <h3 className="text-base font-bold text-zinc-900">{plan.name}</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{plan.description}</p>

                  <div className="flex items-baseline gap-1 my-3">
                    <span className="text-2xl font-black text-zinc-950">₹{plan.amount.toLocaleString()}</span>
                    <span className="text-[10px] font-medium text-zinc-400">/ {plan.duration}</span>
                  </div>

                  <hr className="border-zinc-100 my-3" />

                  <ul className="space-y-2 mb-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[11px] text-zinc-600">
                        <div className="p-0.5 rounded-full bg-zinc-100 text-zinc-900">
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </div>
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  className={`w-full py-2.5 px-3 rounded-xl font-bold text-xs transition-all ${
                    isSelected
                      ? "bg-zinc-950 text-white"
                      : "bg-zinc-100 text-zinc-800"
                  }`}
                >
                  {isSelected ? "Selected Plan" : "Choose Plan"}
                </button>
              </div>
            );
          })}
        </div>

        {/* CHECKOUT SUMMARY[cite: 11] */}
        <div className="bg-white rounded-3xl p-6 border border-zinc-200 shadow-sm space-y-6">
          <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" /> Checkout Summary
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* COUPON & GST */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-zinc-600 block mb-1.5 flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-blue-600" /> Discount Coupon
                </label>

                {appliedCoupon ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-2xl">
                    <div className="flex items-center gap-2 text-xs font-bold text-emerald-800">
                      <Percent className="w-4 h-4" />
                      <span>{appliedCoupon.code} Applied ({appliedCoupon.discountPercent}% OFF)</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="text-xs text-red-600 font-bold hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleApplyCoupon} className="flex items-center gap-1 w-full min-w-0">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="min-w-0 flex-1 px-3.5 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono uppercase text-zinc-900 outline-none focus:border-blue-600"
                    />
                    <button
                      type="submit"
                      className="shrink-0 px-4 py-2.5 bg-zinc-900 text-white font-bold text-xs rounded-xl hover:bg-zinc-800 transition cursor-pointer"
                    >
                      Apply
                    </button>
                  </form>
                )}
              </div>

              <div>
                <label className="text-xs font-bold text-zinc-600 block mb-1.5 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5 text-zinc-400" /> Buyer GSTIN (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 24ALLPD3750G1ZD"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                  className="w-full px-3.5 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono uppercase text-zinc-900 outline-none focus:border-blue-600"
                />
              </div>
            </div>

            {/* PRICE BREAKDOWN[cite: 11] */}
            <div className="bg-zinc-50 border border-zinc-200 p-5 rounded-2xl space-y-2.5 text-xs">
              <div className="flex justify-between text-zinc-600">
                <span>Selected Plan ({currentPlan.name}):</span>
                <span className="font-mono font-bold text-zinc-900">₹{basePrice.toLocaleString()}</span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between text-emerald-600 font-semibold">
                  <span>Discount ({discountPercent}%):</span>
                  <span className="font-mono">-₹{discountAmount.toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between text-zinc-600 border-t border-zinc-200 pt-2">
                <span>Taxable Amount:</span>
                <span className="font-mono font-bold text-zinc-900">₹{taxableAmount.toLocaleString()}</span>
              </div>

              <div className="flex justify-between text-zinc-600">
                <span>GST (18%):</span>
                <span className="font-mono text-zinc-900">₹{gstAmount.toLocaleString()}</span>
              </div>

              <div className="flex justify-between text-sm font-black text-zinc-950 border-t border-zinc-200 pt-2">
                <span>Total Amount:</span>
                <span className="font-mono text-blue-600">₹{finalTotal.toLocaleString()}</span>
              </div>

              <button
                type="button"
                onClick={handleCheckout}
                disabled={loading || !selectedEventId}
                className="w-full mt-3 py-3 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-md transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Processing Payment...</span>
                  </>
                ) : isNativeApp ? (
                  <>
                    <ExternalLink className="w-4 h-4" />
                    <span>Continue to Web Checkout (₹{finalTotal.toLocaleString()})</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    <span>Pay ₹{finalTotal.toLocaleString()} Now</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>

        {/* SECURITY */}
        <div className="flex items-center justify-center gap-2 text-xs text-zinc-400 pt-1">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span>Payments are encrypted.</span>
        </div>

      </div>

      {/* BILL HISTORY MODAL[cite: 11] */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-3xl p-6 shadow-2xl space-y-5 max-h-[85vh] overflow-y-auto relative">
            
            <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-zinc-900">Billing History</h3>
                  <p className="text-xs text-zinc-500">All purchased event passes.</p>
                </div>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 rounded-full bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {loadingHistory ? (
              <div className="py-12 text-center text-xs font-bold text-zinc-400 animate-pulse">
                Fetching invoice records...
              </div>
            ) : invoices.length === 0 ? (
              <div className="py-12 text-center text-zinc-400 text-xs font-medium space-y-2">
                <FileText className="w-8 h-8 text-zinc-300 mx-auto" />
                <p>No billing records found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-4 bg-zinc-50 border border-zinc-200/80 rounded-2xl flex items-center justify-between gap-3 hover:border-blue-300 transition"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-blue-600">{inv.invoice_number}</span>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase rounded-full">
                          {inv.payment_status}
                        </span>
                      </div>
                      <p className="text-xs font-bold text-zinc-900">{inv.plan_name}</p>
                      <p className="text-[11px] text-zinc-500 font-mono">
                        {new Date(inv.created_at).toLocaleDateString()} • Base: ₹{inv.base_amount} | GST: ₹{inv.gst_amount}
                      </p>
                    </div>

                    <div className="text-right shrink-0 space-y-1">
                      <p className="text-sm font-black text-zinc-900">₹{inv.total_amount}</p>
                      <button
                        onClick={() => setSelectedInvoice(inv)}
                        className="px-3 py-1 bg-white border border-zinc-200 text-blue-600 hover:bg-blue-50 text-[11px] font-bold rounded-lg transition cursor-pointer"
                      >
                        Tax Invoice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      )}

      {/* PRINTABLE GST TAX INVOICE MODAL[cite: 11] */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-3xl p-6 shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto relative print:m-0 print:p-0 print:shadow-none print:w-full">
            
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 print:hidden">
              <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Official GST Invoice</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="p-2 rounded-xl bg-blue-50 text-blue-600 font-bold text-xs hover:bg-blue-100 transition flex items-center gap-1 cursor-pointer"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print</span>
                </button>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="p-2 rounded-full bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 pt-1">
              <div className="flex items-start justify-between border-b border-zinc-200 pb-3">
                <div className="space-y-0.5">
                  <h2 className="text-lg font-black text-zinc-900">{SELLER_DETAILS.company}</h2>
                  <p className="text-[10px] text-zinc-500 max-w-[240px] leading-tight">{SELLER_DETAILS.address}</p>
                  <p className="text-[10px] font-mono font-bold text-blue-700 pt-1">GSTIN: {SELLER_DETAILS.gstin}</p>
                  <p className="text-[10px] text-zinc-400">SAC: {SELLER_DETAILS.sac}</p>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-black uppercase bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200">
                    TAX INVOICE
                  </span>
                  <p className="text-xs font-mono font-bold text-zinc-900 mt-2">{selectedInvoice.invoice_number}</p>
                  <p className="text-[10px] text-zinc-500 font-mono">
                    {new Date(selectedInvoice.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-50 rounded-2xl border border-zinc-100 text-xs">
                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block mb-1">Billed To (Customer)</span>
                  <p className="font-bold text-zinc-900">{selectedInvoice.user_name}</p>
                  <p className="text-zinc-500">{selectedInvoice.user_email}</p>
                  {selectedInvoice.user_company && <p className="text-zinc-600 font-semibold">{selectedInvoice.user_company}</p>}
                  {selectedInvoice.user_address && (
                    <p className="text-zinc-500 text-[10px] leading-tight mt-1">{selectedInvoice.user_address}</p>
                  )}
                  {selectedInvoice.gst_number && (
                    <p className="text-blue-600 font-mono font-bold mt-1">GSTIN: {selectedInvoice.gst_number}</p>
                  )}
                </div>
                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase block mb-1">Payment Reference</span>
                  <p className="font-mono font-bold text-zinc-800 text-[11px] truncate">{selectedInvoice.payment_id}</p>
                  <p className="text-emerald-600 font-bold mt-1">Status: {selectedInvoice.payment_status}</p>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-2xl overflow-hidden text-xs">
                <table className="w-full text-left">
                  <thead className="bg-zinc-100 text-zinc-600 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="py-2.5 px-3">Description</th>
                      <th className="py-2.5 px-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 font-medium text-zinc-800">
                    <tr>
                      <td className="py-3 px-3">
                        <div className="font-bold">{selectedInvoice.plan_name}</div>
                        {selectedInvoice.discount_percent > 0 && (
                          <div className="text-[10px] text-emerald-600">Discount Applied ({selectedInvoice.discount_percent}%)</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-mono">₹{selectedInvoice.base_amount}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="space-y-1.5 text-xs border-t border-zinc-200 pt-2 font-medium">
                <div className="flex justify-between text-zinc-600">
                  <span>Base Amount:</span>
                  <span className="font-mono font-bold text-zinc-900">₹{selectedInvoice.base_amount}</span>
                </div>

                {selectedInvoice.discount_amount > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Discount ({selectedInvoice.discount_percent}%):</span>
                    <span className="font-mono">-₹{selectedInvoice.discount_amount}</span>
                  </div>
                )}

                <div className="flex justify-between text-zinc-600">
                  <span>Taxable Value:</span>
                  <span className="font-mono font-bold text-zinc-900">₹{selectedInvoice.taxable_amount}</span>
                </div>

                <div className="flex justify-between text-zinc-600">
                  <span>CGST (9%):</span>
                  <span className="font-mono text-zinc-800">₹{Math.round(selectedInvoice.gst_amount / 2)}</span>
                </div>

                <div className="flex justify-between text-zinc-600">
                  <span>SGST (9%):</span>
                  <span className="font-mono text-zinc-800">₹{Math.round(selectedInvoice.gst_amount / 2)}</span>
                </div>

                <div className="flex justify-between text-emerald-800 bg-emerald-50 p-2.5 rounded-xl border border-emerald-200 font-bold text-sm mt-2">
                  <span>Total Paid (Incl. 18% GST):</span>
                  <span className="font-mono">₹{selectedInvoice.total_amount}</span>
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

    </div>
  );
}